import type { LctDb, MusicTrackRecord } from './db';

/** Limits for "Nhạc trong máy": generous for a playlist, small enough not to fill a phone's storage. */
export const MUSIC_MAX_TRACKS = 50;
export const MUSIC_MAX_BYTES = 30 * 1024 * 1024;

export type MusicTrackInfo = Omit<MusicTrackRecord, 'data'>;

/** What happened to the files the player picked. */
export interface MusicAddResult {
  added: number;
  /** Not audio, too big, or over the track limit. */
  skipped: number;
}

/**
 * The player's own background music (Settings → Nhạc nền → Nhạc trong máy). The files stay in this
 * browser's IndexedDB only: never in the save, its export or a backup, never uploaded anywhere.
 */
export class MusicLibrary {
  constructor(
    private readonly db: LctDb,
    private readonly newId: () => string,
    private readonly now: () => number,
  ) {}

  async list(): Promise<MusicTrackInfo[]> {
    const rows = await this.db.music.orderBy('addedAt').toArray();
    return rows.map((r) => ({ id: r.id, name: r.name, type: r.type, size: r.size, addedAt: r.addedAt }));
  }

  async tracks(): Promise<{ id: string; blob: Blob }[]> {
    const rows = await this.db.music.orderBy('addedAt').toArray();
    return rows.map((r) => ({ id: r.id, blob: new Blob([r.data], { type: r.type }) }));
  }

  async add(files: readonly File[]): Promise<MusicAddResult> {
    let room = MUSIC_MAX_TRACKS - (await this.db.music.count());
    const rows: MusicTrackRecord[] = [];
    for (const file of files) {
      if (room <= 0 || !file.type.startsWith('audio/') || file.size > MUSIC_MAX_BYTES || file.size === 0)
        continue;
      room -= 1;
      rows.push({
        id: this.newId(),
        name: file.name.replace(/\.[a-z0-9]+$/i, ''),
        type: file.type,
        size: file.size,
        addedAt: this.now() + rows.length,
        data: await file.arrayBuffer(),
      });
    }
    if (rows.length > 0) await this.db.music.bulkAdd(rows);
    return { added: rows.length, skipped: files.length - rows.length };
  }

  async remove(id: string): Promise<void> {
    await this.db.music.delete(id);
  }
}
